'use no memo';

import { createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/Card/card';
import { DataTable, DataTablePagination, useTableDisplayState } from '@/components/DataTable';
import Icon, { IconSize } from '@/components/Icon/Icon';
import { HStack, VStack } from '@/components/Layout';
import SearchInput from '@/components/SearchInput';
import { Text } from '@/components/Typography';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { ListFilterTabs, type ListFilterTabItem } from '../-components/ListFilterTabs';
import { isForbiddenError, ListForbiddenState } from '../-components/ListForbiddenState';
import { selectVisibleLedgerScopes, useLedgerDataScopes } from '../-query/useLedgerDataScopes';
import { usePlaceColumns } from './-columns';
import { PlaceDeviceSection } from './-components/PlaceDeviceSection';
import { PlaceEquipmentSection } from './-components/PlaceEquipmentSection';
import { usePlaceLedgerQuery } from './-query';
import { SearchSchema } from './-zod-schema';
type PlaceDetailTab = 'devices' | 'equipments';
type PlaceScope = 'managed-place';
export const Route = createFileRoute('/_individual/individual/places/')({
  validateSearch: zodValidator(SearchSchema),
  component: PlacesPage
});
function PlacesPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.places'
  });
  const {
    t: tc
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.common'
  });

  // 使用可能なデータスコープはセッションに畳み込まれている（DC-8324）。
  const dataScopes = useLedgerDataScopes();
  const visibleScopes = useMemo(() => selectVisibleLedgerScopes(dataScopes, 'place').filter((scope): scope is PlaceScope => scope !== 'self'), [dataScopes]);
  const effectiveScope: PlaceScope = visibleScopes.includes(search.scope) ? search.scope : visibleScopes[0] ?? 'managed-place';
  useEffect(() => {
    if (visibleScopes.length > 0 && search.scope !== effectiveScope) {
      void navigate({
        search: prev => ({
          ...prev,
          scope: effectiveScope
        }),
        replace: true
      });
    }
  }, [visibleScopes.length, search.scope, effectiveScope, navigate]);

  // 拠点台帳は managed-place scope のみ（self タブは無い）。granted な scope が
  // 無い（＝権限で閲覧拒否 or 管掌拠点 0 件）場合は他台帳と同様に権限エラーへ倒す。
  // scope が無いのに API を叩くと 403/500 を招くため、クエリ自体を無効化する（DC-8768）。
  const {
    data,
    isPending,
    isError,
    error
  } = usePlaceLedgerQuery({
    scope: effectiveScope
  }, {
    enabled: visibleScopes.length > 0
  });
  const columns = usePlaceColumns();
  const allRows = useMemo(() => data?.items ?? [], [data]);
  const [keyword, setKeyword] = useState('');
  const rows = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (kw === '') {
      return allRows;
    }
    return allRows.filter(p => `${p.name} ${p.code ?? ''}`.toLowerCase().includes(kw));
  }, [allRows, keyword]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20
  });

  // oxlint-disable-next-line react-hooks-js/incompatible-library -- Compiler非互換のサードパーティAPI
  const table = useReactTable({
    data: rows,
    columns,
    state: {
      pagination
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: true
  });
  const displayState = useTableDisplayState(isPending, rows);
  const selected = search.id != null ? allRows.find(p => p.id === search.id) ?? null : null;
  const [resourceTab, setResourceTab] = useState<PlaceDetailTab>('equipments');
  const scopeLabels: Record<PlaceScope, string> = {
    'managed-place': t('tabs.managedPlace')
  };
  const tabs: ListFilterTabItem<PlaceScope>[] = visibleScopes.map(scope => ({
    value: scope,
    label: scopeLabels[scope]
  }));
  // granted な scope が 1 つでもあればタブを表示する（拠点は managed-place のみ）。
  const showScopeTabs = tabs.length > 0;
  const resourceTabs: ListFilterTabItem<PlaceDetailTab>[] = selected == null ? [] : [{
    value: 'devices',
    label: t('detail.tabs.devices'),
    count: selected.deviceCount
  }, {
    value: 'equipments',
    label: t('detail.tabs.equipments'),
    count: selected.equipmentCount
  }];

  // granted な scope が 1 つも無い（閲覧拒否 or 管掌拠点 0 件）／ledger 側 403 は
  // 他台帳と同様に権限エラーとして扱う（DC-8768。managed-place のみの拠点台帳では
  // scope 0 件＝閲覧すべき拠点なし、を即座に権限エラーへ倒す）。
  if (visibleScopes.length === 0 || isError && isForbiddenError(error)) {
    return <div className="flex h-full min-w-0 flex-col overflow-hidden">
        <PageHeader title={t('pageTitle')} />
        <VStack className="min-h-0 flex-1 gap-4 px-5 py-6 md:px-12 md:py-8">
          <ListForbiddenState onBack={() => void navigate({
          to: '/individual'
        })} />
        </VStack>
      </div>;
  }
  if (isError) {
    throw error;
  }
  if (selected != null) {
    // リソースタブは各セクションの検索バーと同じ行（HStack）に並べたいので、
    // タブ要素を生成してセクションへ渡す（他の一覧画面とレイアウトを揃える）。
    const resourceTabsSlot = <ListFilterTabs value={resourceTab} tabs={resourceTabs} onChange={setResourceTab} />;
    return <div className="flex h-full min-w-0 flex-col overflow-hidden">
        <PageHeader title={selected.name} subtitle={selected.location} />
        <VStack className="min-h-0 flex-1 gap-4 px-5 py-6 md:px-12 md:py-8">
          {resourceTab === 'equipments' ? <PlaceEquipmentSection placeId={selected.id} tabsSlot={resourceTabsSlot} /> : <PlaceDeviceSection placeId={selected.id} tabsSlot={resourceTabsSlot} />}
        </VStack>
      </div>;
  }
  return <div className="flex h-full min-w-0 flex-col overflow-hidden">
      <PageHeader title={t('pageTitle')} />
      <VStack className="min-h-0 flex-1 gap-4 px-5 py-6 md:px-12 md:py-8">
        <HStack className="items-center gap-2">
          {showScopeTabs && <ListFilterTabs value={effectiveScope} tabs={tabs} onChange={scope => navigate({
          search: prev => ({
            ...prev,
            scope,
            id: undefined
          })
        })} />}
          <div className="w-full max-w-xs">
            <SearchInput value={keyword} onChange={e => setKeyword(e.target.value)} placeholder={tc('search')} />
          </div>
        </HStack>
        <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <DataTable table={table} displayState={displayState} isHoverable onRowClick={row => navigate({
          search: prev => ({
            ...prev,
            id: row.original.id
          })
        })} />
        </Card>
        <DataTablePagination table={table} totalCount={rows.length} pageOptions={[20, 50, 100]} />
      </VStack>
    </div>;
}
function PageHeader({
  title,
  subtitle
}: {
  title: string;
  subtitle?: string | null;
}) {
  return <div className="flex shrink-0 items-center gap-3 border-b border-neutral-muted-border bg-neutral-fill px-5 py-3 shadow-sm md:px-12 md:py-4">
      <span className="text-neutral-on-fill">
        <Icon iconName="Building2" size={IconSize.XL} inheritColor />
      </span>
      <div>
        <Text as="h1" variant="h3">
          {title}
        </Text>
        {subtitle != null && <Text variant="pSmall" className="text-neutral-muted-on-fill">
            {subtitle}
          </Text>}
      </div>
    </div>;
}